/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useMemo } from 'react';
import { PlaidLinkStableEvent, PlaidLinkOnEventMetadata } from 'react-plaid-link';
import { WidgetConfigs } from 'aerosync-web-sdk';
import { getLogger } from 'src/utils/datadog-logger';

import _ from 'lodash';

import { tracker, Events } from 'utils/analytics';
import { useGetInstoreAnalyticsTokenQuery } from 'types/graphql';
import {
  AeroSyncPreAuthEvent,
  AeroSyncPreAuthEventMetadata,
  EnrollmentSteps,
  LOG_NAMESPACES,
  PlaidLinkCustomizationNames,
} from 'src/payments/constants';
import { getPersistedValue, setPersistedValue } from 'shared/utils/persisted-values';
import { useRouter } from 'next/router';
import { INSTORE_ENROLLMENT_ROUTE, INSTORE_ENROLLMENT_SOURCE } from 'src/components/instore-enrollment';

export type EnrollmentEvents = keyof Pick<
  Events,
  | 'dutchiePayAeroSyncOnEvent'
  | 'dutchiePayConnectBankStep'
  | 'dutchiePayCTAButtonClicked'
  | 'dutchiePayEnrollmentButtonClicked'
  | 'dutchiePayEnrollmentStep'
  | 'dutchiePayLearnMoreButtonClicked'
  | 'dutchiePayPlaidOnEvent'
  | 'dutchiePayPreAuthEvent'
>;

export type InstoreCartEvents = keyof Pick<
  Events,
  | 'dutchiePayInstoreAccountLinkViewed'
  | 'dutchiePayInstoreCartApprovalClicked'
  | 'dutchiePayInstoreCartApprovalFailed'
  | 'dutchiePayInstoreCartApprovalSucceeded'
  | 'dutchiePayInstoreCartViewed'
  | 'dutchiePayInstoreLoginViewed'
>;

export type DutchiePayEvents = EnrollmentEvents | InstoreCartEvents;

export const ENROLLMENT_EVENTS = {
  CTA_BUTTON_CLICKED: 'dutchiePayCTAButtonClicked',
  CONNECT_BANK_STEP: 'dutchiePayConnectBankStep',
  ENROLLMENT_BUTTON_CLICKED: 'dutchiePayEnrollmentButtonClicked',
  ENROLLMENT_STEP: 'dutchiePayEnrollmentStep',
  LEARN_MORE_CLICKED: 'dutchiePayLearnMoreButtonClicked',
  PLAID_ON_EVENT: 'dutchiePayPlaidOnEvent',
  AEROSYNC_ON_EVENT: 'dutchiePayAeroSyncOnEvent',
  AEROSYNC_PRE_AUTH_EVENT: 'dutchiePayPreAuthEvent',
} as const;

export const INSTORE_EVENTS = {
  ACCOUNT_LINK_VIEWED: 'dutchiePayInstoreAccountLinkViewed',
  CART_APPROVAL_CLICKED: 'dutchiePayInstoreCartApprovalClicked',
  CART_APPROVAL_FAILED: 'dutchiePayInstoreCartApprovalFailed',
  CART_APPROVAL_SUCCEEDED: 'dutchiePayInstoreCartApprovalSucceeded',
  CART_VIEWED: 'dutchiePayInstoreCartViewed',
  LOGIN_VIEWED: 'dutchiePayInstoreLoginViewed',
} as const;

export const EVENT_ACTIONS = {
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  SUBMITTED: 'Submitted',
  SUCCEEDED: 'Succeeded',
  VIEWED: 'Viewed',
} as const;

export type EnrollmentEventsPayload = {
  step?: string;
  action?: string;
  source?: string;
  buttonLocation?: string;
  experimentFlagName?: string;
  experimentVariant?: string;
};

export type InstoreEventsPayload = {
  instoreCartId?: string;
  loginStep?: string;
  dispensary?: {
    id?: string;
    name?: string;
  };
  showBarcode?: boolean;
};

export enum GuestEnrollmentFunnelExperimentVariant {
  CONTROL = 'Control',
  EXPERIMENTAL = 'Experimental',
}

export const DPAY_SESSION_TOKEN_KEY = 'dutchiePaySessionToken';

export const ENROLLMENT_STEPS_TO_EXPERIMENT_METRICS = {
  [EnrollmentSteps.emailForm]: {
    [EVENT_ACTIONS.VIEWED]: 'fintech.pay-signup.page-viewed',
  },
  [EnrollmentSteps.connectBank]: {
    [EVENT_ACTIONS.VIEWED]: 'fintech.pay-signup.connect-bank-viewed',
    [EVENT_ACTIONS.SUCCEEDED]: 'fintech.pay-signup.connect-bank-succeeded',
  },
  [EnrollmentSteps.terms]: {
    [EVENT_ACTIONS.VIEWED]: 'fintech.pay-signup.terms-modal-viewed',
    [EVENT_ACTIONS.SUBMITTED]: 'fintech.pay-signup.terms-modal-accepted',
  },
  [EnrollmentSteps.completeEnrollment]: {
    [EVENT_ACTIONS.VIEWED]: 'fintech.pay-signup.enrollment-completed',
  },
  [EnrollmentSteps.alreadySignedUp]: {
    [EVENT_ACTIONS.VIEWED]: 'fintech.pay-signup.already-enrolled',
  },
};

export const PAY_SIGNUP_FUNNEL_STEPS = [
  'fintech.checkout.link-your-bank-clicked',
  'fintech.pay-signup.page-viewed',
  'fintech.pay-signup.connect-bank-viewed',
  'fintech.pay-signup.connect-bank-succeeded',
  'fintech.pay-signup.terms-modal-viewed',
  'fintech.pay-signup.terms-modal-accepted',
  'fintech.pay-signup.enrollment-completed',
  'fintech.checkout.pay-by-bank-order-submitted',
];

export type AnalyticsEventPayload = EnrollmentEventsPayload | InstoreEventsPayload;

export type DutchiePayEventTracker = (methodName: DutchiePayEvents, payloadProps?: AnalyticsEventPayload) => void;
export type EnrollmentStepTracker = (step: EnrollmentSteps) => void;
export type PlaidOnEventTracker = (
  eventName: PlaidLinkStableEvent,
  metadata: PlaidLinkOnEventMetadata,
  linkCustomizationName: PlaidLinkCustomizationNames
) => void;
export type AeroSyncOnEventTracker = WidgetConfigs['onEvent'];
export type PreAuthEventTracker = (
  eventName: AeroSyncPreAuthEvent,
  eventMetadata?: AeroSyncPreAuthEventMetadata
) => void;

const logger = getLogger(LOG_NAMESPACES.PBB);

export function trackEvent(token, methodName, payloadProps): void {
  if (!Object.values({ ...ENROLLMENT_EVENTS, ...INSTORE_EVENTS }).includes(methodName)) {
    const errorMessage = `Invalid Analytics Tracking Method Name: ${String(methodName)}`;
    console.error(errorMessage);
    logger.error('Invalid analytics tracking method name', {
      methodName: String(methodName ?? 'unknown'),
      hasToken: Boolean(token),
      hasPayloadProps: Boolean(payloadProps),
    });
    return;
  }

  const payload = {
    instoreCartToken: token, // Delete after data team has updated to dutchiePaySessionToken
    dutchiePaySessionToken: token,
    ...payloadProps,
  };

  const method = tracker[methodName];

  if (token) {
    try {
      method(payload);
    } catch (err) {
      logger.error('Failed to track analytics event', {
        error: err?.message ?? 'Unknown error',
        methodName: methodName ?? 'unknown',
        hasToken: Boolean(token),
        hasPayloadProps: Boolean(payloadProps),
      });
    }
  }
}

export type UseDutchiePayAnalyticsResponse = {
  dutchiePayEventTracker: DutchiePayEventTracker;
  trackEnrollmentStepSubmitted: EnrollmentStepTracker;
  trackEnrollmentStepViewed: EnrollmentStepTracker;
  trackPlaidOnEvent: PlaidOnEventTracker;
  trackAeroSyncOnEvent: AeroSyncOnEventTracker;
  trackPreAuthEvent: PreAuthEventTracker;
};
// TODO: Get dispo data here https://dutchie.atlassian.net/browse/ENG-47647
export const useDutchiePayAnalytics = (): UseDutchiePayAnalyticsResponse => {
  const router = useRouter();

  const enrollmentSource = useMemo(() => {
    const path = router.asPath;
    return path.includes(INSTORE_ENROLLMENT_ROUTE) ? INSTORE_ENROLLMENT_SOURCE : router.query.enrollmentSource;
  }, [router.asPath, router.query.enrollmentSource]);

  /*
    Check session storage for token, and if not found then request a new one and persist it
  */
  const dutchiePaySessionToken = getPersistedValue(DPAY_SESSION_TOKEN_KEY, '');

  // TODO: Rename the query to GetDutchiePaySessionToken https://dutchie.atlassian.net/browse/ENG-47643
  const { data, error } = useGetInstoreAnalyticsTokenQuery({ skip: Boolean(dutchiePaySessionToken) });
  const analyticsToken = data?.getInstoreAnalyticsToken?.instoreAnalyticsTokenId;

  if (error) {
    console.error(`Failed to fetch analytics token. Error:${error.message}`);
    logger.error('Failed to fetch analytics token', {
      error: error.message,
      errorCode: error.graphQLErrors[0]?.extensions?.code ?? 'NO_CODE',
      enrollmentSource: enrollmentSource ?? 'unknown',
      step: 'analytics_token_fetch',
    });
  }

  if (analyticsToken) {
    setPersistedValue(DPAY_SESSION_TOKEN_KEY, analyticsToken, true);
  }

  const eventToken = dutchiePaySessionToken || analyticsToken;

  /*
    Use for general DPay events
  */
  const dutchiePayEventTracker: DutchiePayEventTracker = useCallback(
    (methodName, payloadProps) => trackEvent(eventToken, methodName, payloadProps),
    [eventToken]
  );

  /*
    Use for specific enrollment step viewed events
  */
  const trackEnrollmentStepViewed = useCallback(
    (step: EnrollmentSteps): void =>
      trackEvent(eventToken, ENROLLMENT_EVENTS.ENROLLMENT_STEP, {
        step,
        action: EVENT_ACTIONS.VIEWED,
        enrollmentSource,
      }),
    [eventToken, enrollmentSource]
  );

  /*
    Use for specific enrollment step submitted events
  */
  const trackEnrollmentStepSubmitted = useCallback(
    (step: EnrollmentSteps): void =>
      trackEvent(eventToken, ENROLLMENT_EVENTS.ENROLLMENT_STEP, {
        step,
        action: EVENT_ACTIONS.SUBMITTED,
        enrollmentSource,
      }),
    [eventToken, enrollmentSource]
  );

  const trackPlaidOnEvent = useCallback(
    (
      eventName: PlaidLinkStableEvent,
      metadata: PlaidLinkOnEventMetadata,
      linkCustomizationName: PlaidLinkCustomizationNames
    ) => {
      const eventMetadata = _.omitBy(metadata, (val) => val == null);

      trackEvent(eventToken, ENROLLMENT_EVENTS.PLAID_ON_EVENT, {
        eventName,
        eventMetadata,
        linkCustomizationName,
      });

      if (eventName === PlaidLinkStableEvent.ERROR) {
        logger.error('Plaid error', { eventName, eventMetadata });
      }
    },
    [eventToken]
  );

  // TODO: Add get name of Aerosync error type and add logging
  const trackAeroSyncOnEvent: AeroSyncOnEventTracker = useCallback(
    ({ payload, type }) => {
      trackEvent(eventToken, ENROLLMENT_EVENTS.AEROSYNC_ON_EVENT, {
        eventName: type,
        eventMetadata: payload,
      });
    },
    [eventToken]
  );

  const trackPreAuthEvent: PreAuthEventTracker = useCallback(
    (eventName, eventMetadata) => {
      trackEvent(eventToken, ENROLLMENT_EVENTS.AEROSYNC_PRE_AUTH_EVENT, { eventName, eventMetadata });
    },
    [eventToken]
  );

  return {
    dutchiePayEventTracker,
    trackEnrollmentStepSubmitted,
    trackEnrollmentStepViewed,
    trackPlaidOnEvent,
    trackAeroSyncOnEvent,
    trackPreAuthEvent,
  };
};
